import { broncoURLENV } from "../ENV";

const legacyCategoryMap = {
    'Restaurant': 13065,
    'Cafe, Coffee, and Tea House': 13032,
    'Lodging': 19009,
    'Bank': 11045,
    'Fuel Station': 19007,
    'Convenience Store': 17029,
    'ATM': 11044,
    'Grocery Store': 17069,
    'Pharmacy': 17035,
    'Shopping Mall': 17114,
    'Gym and Studio': 18021,
    'Health and Beauty Service': 11061,
    'Spa': 11073,
    'Florist': 17056,
    'Gift Store': 17089,
    'Hospital': 15014,
    'Post Office': 12075,
    'Rail Station': 19047,
    'Car Wash and Detail': 11011,
    'Spiritual Center': 12098,
    'Library': 12080,
    'Park': 16032,
    'Museum': 10027,
    'Movie Theater': 10024,
    'Bar': 13003,
    'Night Club': 10032,
    'Liquor Store': 17076,
    'Bakery': 13002,
    'Bookstore': 17018,
    'Dry Cleaner': 11066,
    'Laundry Service': 11069,
    'Clothing Store': 17043,
    'Computers and Electronics Retail': 17023,
    'Hardware Store': 17090,
};
		
const RATE_LIMIT_RETRIES = 2;
const RATE_LIMIT_FALLBACK_WAIT_MS = 1500;
const RATE_LIMIT_MAX_WAIT_MS = 3000;

const REQUEST_TIMEOUT_MS = 8000;
const TOTAL_DEADLINE_MS  = 15000;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export const retryAfterMs = (res) => {
    const header = res && res.headers && res.headers.get && res.headers.get('Retry-After');
    const seconds = parseFloat(header);
    if (!isFinite(seconds) || seconds <= 0) {
        return RATE_LIMIT_FALLBACK_WAIT_MS;
    }
    return Math.min(seconds * 1000, RATE_LIMIT_MAX_WAIT_MS);
};

const fetchJsonWithRateLimitRetry = async (url, options) => {
    const startedAt = Date.now();

    for (let attempt = 0; ; attempt++) {
        let waitBeforeRetry = 0;

        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        const timeout = setTimeout(() => controller && controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            const res = await fetch(url, controller ? { ...options, signal: controller.signal } : options);

            const outOfRetries = attempt >= RATE_LIMIT_RETRIES;
            const outOfTime    = (Date.now() - startedAt) >= TOTAL_DEADLINE_MS;
            if (res.status !== 429 || outOfRetries || outOfTime) {
                if (!res.ok) {
                    return { ok: false, status: res.status };
                }
                return { ok: true, status: res.status, data: await res.json() };
            }
            waitBeforeRetry = retryAfterMs(res);
        } finally {
            clearTimeout(timeout);
        }

        await sleep(waitBeforeRetry);
    }
};

// Returns null on failure and [] only for a genuine empty result. Callers cache on
// truthiness, so returning [] for an error would cache the failure (see AmenitiesMap
// resultCache) and the category would stay blank with no retry.
export const fetchGoogleNearbyPlaces = async (category, radius, latitude, longitude, limit = 20) => {
    const url = `${broncoURLENV}/google_nearby_search`;
    const body = {
        category_id: category,
        radius,
        latitude,
        longitude,
        limit,
    };
    try {
        let session = null;
        try {
            session = JSON.parse(window.localStorage.getItem('session') || 'null');
        } catch {
            session = null;
        }
        const headers = {
            'Content-Type': 'application/json',
        };
        if (session && session.session) {
            headers['X-Auth-Session'] = session.session;
        }

        const res = await fetchJsonWithRateLimitRetry(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });

        if (!res.ok) {
            console.error('fetchGoogleNearbyPlaces HTTP error', res.status);
            return null;
        }
        return Array.isArray(res.data) ? res.data : null;
    } catch (err) {
        console.error('fetchGoogleNearbyPlaces error', err);
        return null;
    }
};

export const fetchGooglePlaceDetails = async (placeId) => {
    if (!placeId) { return null; }
    const url = `${broncoURLENV}/google_place_details?place_id=${encodeURIComponent(placeId)}`;
    try {
        let session = null;
        try {
            session = JSON.parse(window.localStorage.getItem('session') || 'null');
        } catch {
            session = null;
        }
        const headers = {};
        if (session && session.session) {
            headers['X-Auth-Session'] = session.session;
        }
        const res = await fetchJsonWithRateLimitRetry(url, { headers });
        if (!res.ok) {
            console.error('fetchGooglePlaceDetails HTTP error', res.status);
            return null;
        }
        return res.data;
    } catch (err) {
        console.error('fetchGooglePlaceDetails error', err);
        return null;
    }
};

export const legacyFetchPlaces = async (legacyCategory, radius, latitude, longitude, limit = 20) => {
    const category = `${legacyCategoryMap[legacyCategory]}`;
   
    let results;

    const body = JSON.stringify({
        categories: category,
        latitude,
        longitude,
        radius,
        limit,
    });

    try {
        const url = `${broncoURLENV}/fsq_search`;
        const response = await fetch(url,	{
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body,
        });

        if (!response.ok) {
            console.error(`fsq_search returned ${response.status} for category "${category}"`);
            return;
        }

        const data = await response.json();

        if (!Array.isArray(data?.results)) {
            console.error(`fsq_search response missing results array for category "${category}"`, data);
            return;
        }

        results = data.results.sort((resultA, resultB) => {
            if (resultA.name > resultB.name) {
                return 1;
            }
            return -1;
        });
    } catch(error) {
        console.error(`fsq_search failed for category "${category}":`, error);
    } finally {
        return results;
    }
};

export const fetchPlaces = async (category, radius, latitude, longitude, limit = 20) => {
    let results;

    const body = JSON.stringify({
        categories: category,
        latitude,
        longitude,
        limit,
        radius,
    });

    try {
        const url = `${broncoURLENV}/fsq_search`;
        const response = await fetch(url,	{
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body,
        });

        if (!response.ok) {
            console.error(`fsq_search returned ${response.status} for category "${category}"`);
            return;
        }

        const data = await response.json();

        if (!Array.isArray(data?.results)) {
            console.error(`fsq_search response missing results array for category "${category}"`, data);
            return;
        }

        results = data.results.sort((resultA, resultB) => {
            if (resultA.name > resultB.name) {
                return 1;
            }
            return -1;
        });
    } catch(error) {
        console.error(`fsq_search failed for category "${category}":`, error);
    } finally {
        return results;
    }
};